import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { PlatformLayout } from "./platform-layout";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PlatformGmailConnector } from "@/components/platform/platform-gmail-connector";
import { Settings, Loader2, Save, Calendar, CheckCircle, Link as LinkIcon } from "lucide-react";

interface PlatformUser {
  id: string;
  email: string;
  name: string;
  role?: string;
}

type CalendarStatus = { connected: boolean; email: string | null };

export default function PlatformAccountPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [location, setLocation] = useLocation();

  // After returning from Gmail or Google Calendar OAuth, the URL carries
  // a ?gmail=connected or ?user_calendar=connected param. Invalidate the
  // relevant status queries so the UI reflects the connection immediately
  // without requiring a manual page reload, then strip the param.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const gmailParam = params.get("gmail");
    const calParam = params.get("user_calendar");
    let changed = false;
    if (gmailParam) {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/gmail/status"] });
      params.delete("gmail");
      changed = true;
    }
    if (calParam) {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/calendar/status"] });
      params.delete("user_calendar");
      changed = true;
    }
    if (changed) {
      const newSearch = params.toString();
      setLocation(window.location.pathname + (newSearch ? `?${newSearch}` : ""), { replace: true });
    }
  }, []);

  const { data: me, isLoading: meLoading } = useQuery<{ user: PlatformUser }>({
    queryKey: ["/api/platform/me"],
  });

  const [name, setName] = useState("");
  useEffect(() => {
    if (me?.user?.name != null) setName(me.user.name);
  }, [me?.user?.name]);

  const nameDirty = me ? name.trim() !== (me.user.name || "") : false;

  const profileMutation = useMutation({
    mutationFn: async () => (await apiRequest("PATCH", "/api/platform/me/profile", { name: name.trim() })).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/me"] });
      toast({ title: "Saved", description: "Your profile has been updated." });
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to update profile", variant: "destructive" }),
  });

  // ── Personal Google Calendar ──
  const { data: calStatus, isLoading: calLoading } = useQuery<CalendarStatus>({
    queryKey: ["/api/platform/calendar/status"],
  });

  const connectCalMutation = useMutation({
    mutationFn: async () => {
      const url = window.location.pathname + window.location.search;
      const res = await apiRequest("GET", `/api/platform/calendar/auth?returnUrl=${encodeURIComponent(url)}`);
      return res.json();
    },
    onSuccess: (data: { authUrl: string }) => { window.location.href = data.authUrl; },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to start Calendar connect", variant: "destructive" }),
  });

  const disconnectCalMutation = useMutation({
    mutationFn: async () => (await apiRequest("POST", "/api/platform/calendar/disconnect")).json(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/platform/calendar/status"] });
      toast({ title: "Disconnected", description: "Google Calendar disconnected." });
    },
    onError: (e: any) => toast({ title: "Error", description: e?.message || "Failed to disconnect", variant: "destructive" }),
  });

  return (
    <PlatformLayout>
      <div className="space-y-6" data-testid="page-platform-account">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Settings className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold">Settings</h1>
            <p className="text-sm text-muted-foreground">Manage your personal profile and connected accounts.</p>
          </div>
        </div>

        {/* Profile */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Profile</CardTitle>
            <CardDescription>Your display name and login email.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {meLoading ? (
              <div className="flex items-center gap-2 text-sm text-muted-foreground py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Loading…
              </div>
            ) : (
              <>
                <div className="space-y-1.5 max-w-md">
                  <Label htmlFor="profile-name">Name</Label>
                  <Input
                    id="profile-name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="input-profile-name"
                  />
                </div>
                <div className="space-y-1.5 max-w-md">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input id="profile-email" value={me?.user.email || ""} disabled data-testid="input-profile-email" />
                  <p className="text-[11px] text-muted-foreground">Email is your login identity and can't be changed here.</p>
                </div>
                <Button
                  size="sm"
                  disabled={!nameDirty || !name.trim() || profileMutation.isPending}
                  onClick={() => profileMutation.mutate()}
                  data-testid="button-save-profile"
                >
                  {profileMutation.isPending ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1.5" />}
                  Save changes
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Connections */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
                <LinkIcon className="w-4 h-4 text-primary" />
              </div>
              <div>
                <CardTitle className="text-base">Connections</CardTitle>
                <CardDescription>Connect Gmail and Google Calendar for your platform account.</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-1 pt-0">
            <div className="border-b border-border/50">
              <PlatformGmailConnector returnUrl="/platform/account" />
            </div>

            {/* Personal Calendar */}
            <div className="flex items-center justify-between py-2">
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-muted-foreground" />
                <div>
                  <span className="font-medium text-sm">Google Calendar</span>
                  {calStatus?.connected && calStatus?.email ? (
                    <p className="text-xs text-muted-foreground" data-testid="text-platform-calendar-email">{calStatus.email}</p>
                  ) : (
                    <p className="text-xs text-muted-foreground">Your personal calendar for availability.</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                {calLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                ) : calStatus?.connected ? (
                  <>
                    <span className="flex items-center gap-1 text-xs text-green-600">
                      <CheckCircle className="w-3.5 h-3.5" /> Connected
                    </span>
                    <Button
                      size="sm" variant="ghost"
                      onClick={() => disconnectCalMutation.mutate()}
                      disabled={disconnectCalMutation.isPending}
                      data-testid="button-platform-disconnect-calendar"
                    >
                      {disconnectCalMutation.isPending ? <Loader2 className="w-3 h-3 animate-spin" /> : "Disconnect"}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    onClick={() => connectCalMutation.mutate()}
                    disabled={connectCalMutation.isPending}
                    data-testid="button-platform-connect-calendar"
                  >
                    {connectCalMutation.isPending && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                    Connect
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </PlatformLayout>
  );
}
